import React from "react"
import Skeleton from "react-loading-skeleton"
import "./Card.scss"
import { CardProps } from "../../types"
import EventBadge from "../events/EventBadge"

const Card: React.FC<CardProps> = ({
  id,
  title,
  subtitle,
  description,
  note,
  image,
  isFeatured = false,
  isEvent = false,
  isApproved = true,
  dateTime,
  endDate,
  link,
  orientation = "vertical",
  styleVariant = "basic",
  size = "base",
  showButton = false,
  buttonText = "View",
  onCardClick,
  showAddress = false,
  address,
  showDescription = false,
  showNote = false,
  showVibes = false,
  vibes,
  categories,
  price,
  isLoading = false,
}) => {
  const handleClick = () => {
    if (onCardClick) onCardClick(id)
    if (link) window.location.href = link
  }

  const renderVibes = () => {
    if (vibes && vibes.length > 0) {
      return (
        <ul className="vibes">
          {vibes.slice(0, 3).map((vibe, index) => (
            <li className="vibe" key={index}>
              {typeof vibe === "object" ? vibe.name : vibe}
            </li>
          ))}
        </ul>
      )
    }
    return null
  }

  const renderPrice = () => {
    if (price) {
      return <div className="price">{typeof price === "number" ? `$${price.toFixed(2)}` : price}</div>
    }
    return null
  }

  const renderBadge = () => {
    if (isFeatured) {
      return <span className="featured-badge">Featured</span>
    } else if (!isApproved) {
      return <span className="featured-badge pending">Not Approved</span>
    } else if (price === "FREE") {
      return <span className="featured-badge free">Free</span>
    }
    return null
  }

  const renderEventBadge = () => {
    if (isEvent && dateTime) {
      return <EventBadge dateTime={dateTime} />
    }
    return null
  }

  const cardClassName = `sing-card o-${orientation} s-${styleVariant} ${size} ${isFeatured ? "featured" : ""}`

  if (isLoading) {
    return (
      <div className={cardClassName}>
        <div className="sing-card-inner">
          <Skeleton height={orientation === "vertical" ? 200 : 150} />
          <div className="sing-card-bottom">
            <Skeleton count={3} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <article id={id} className={cardClassName} onClick={handleClick}>
        <div className="sing-card-inner">
          {orientation === "vertical" ? (
            <>
              {renderEventBadge()}
              {renderBadge()}
              {image && (
                <div className="sing-card-image">
                  <div
                    className="background-image"
                    style={{
                      backgroundImage: `url(${image})`,
                      content: `url(${image})`,
                      backgroundSize: "cover",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "center center",
                      objectFit: "cover",
                    }}
                  />
                </div>
              )}
              <div className="sing-card-bottom">
                {categories && categories.length > 0 && (
                  <span className="category">{typeof categories[0] === "object" ? categories[0].name : categories[0]}</span>
                )}
                <h4 className="title">{title}</h4>
                {showAddress && address && <div className="address">{address}</div>}
                {showNote && note && <div className="note">{note}</div>}
                {showDescription && description && <div className="description">{description}</div>}
                {renderPrice()}
                {showVibes && renderVibes()}
                {showButton && <button className="card-button">{buttonText}</button>}
              </div>
            </>
          ) : (
            <div className="columns is-flex-tablet is-gapless">
              <div className="column is-4 image is-flex-tablet">
                <div
                  className="sing-card-image"
                  style={{
                    backgroundImage: `url(${image})`,
                    content: `url(${image})`,
                    backgroundSize: "cover",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center center",
                    objectFit: "cover",
                  }}
                />
              </div>
              <div className="column is-8 text is-flex-tablet">
                <div className="inner-column sing-card-bottom">
                  {renderEventBadge()}
                  {renderBadge()}
                  {categories && categories.length > 0 && (
                    <span className="category">{typeof categories[0] === "object" ? categories[0].name : categories[0]}</span>
                  )}
                  {subtitle && <div className="subtitle">{subtitle}</div>}
                  <h4 className="title">{title}</h4>
                  {showAddress && address && <div className="address">{address}</div>}
                  {showNote && note && <div className="note">{note}</div>}
                  {showDescription && description && <div className="description">{description}</div>}
                  {renderPrice()}
                  {showVibes && renderVibes()}
                  {showButton && <button className="card-button">{buttonText}</button>}
                </div>
              </div>
            </div>
          )}
        </div>
      </article>
    </div>
  )
}

export default Card
